import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { Box, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { ListItemBox } from "lib/@components/ListItemBox"
import { Repeat } from "lib/@components/repeat"
import { SORT_FROM_CLASSIFICATION } from "library/SFG20/constants.mjs"
import { ColorCount } from "routes/plan/plugins/review-differences/color-count"
import { PageSize } from "lib/@components/pagination/page-size"
import { SortOn } from "routes/asset-register/plugins/regime-display/sort-on"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { NoResults } from "lib/@components/no-results/no-results"
import { TaskItem } from "routes/plan/plugins/review-differences/task-item"
import { formatTime } from "lib/format-time"
import { PaginationPortal } from "lib/@components/pagination-portal"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

export function SingleListOfChanges({ results, label, type, prefix }) {
    const { isChangesFromLibrary } = useBoundContext()
    const [take, setTake] = useBoundValue("take", 10)
    const [, setSkip] = useBoundValue(`${type}Skip`, 0)
    const theme = useTheme()
    const isRemoved = label === "Removed Tasks"
    const isAdded = label === "Additional Tasks"

    let subtitle = ""

    if (isRemoved) {
        subtitle = "Tasks that have been removed that are in the SFG20 schedule"
    } else if (isAdded) {
        subtitle = "Tasks that have been added that are not included in the SFG20 schedule"
    }

    return (
        <Bound type={type} prefix={prefix}>
            <Divider sx={{ py: 1, mb: 1 }} />
            <Box pb={2}>
                <ListItemBox spacing={2} data-cy={`${type}-single-list-changes-summary`}>
                    <Typography component="div" variant="subtitle1">
                        {label}
                    </Typography>
                    <Typography component="div" variant="subtitle1" color="text.secondary">
                        ({results.tasks.total})
                    </Typography>
                    <Typography component="div" variant="subtitle1" color="text.secondary">
                        {formatTime(results.minutes)} per annum
                    </Typography>
                    <Box flex={1} />
                    <Repeat
                        list={results.summarise.summary.sortBy((c) => SORT_FROM_CLASSIFICATION[c.value])}
                        item={<ColorCount />}
                    />
                </ListItemBox>
                {isChangesFromLibrary && (
                    <ListItemBox>
                        <Typography variant="caption" color="text.secondary">
                            {subtitle}
                        </Typography>
                    </ListItemBox>
                )}
                <ListItemBox spacing={1} mt={2} data-cy={`changes-review-pagination-${type}-table`}>
                    <Box flex={1} />
                    <PageSize setTake={setTake} take={take} />
                    <PaginationPortal />
                </ListItemBox>

                <Box
                    width={1}
                    sx={{
                        width: 1,
                        flex: 1,
                        maxHeight: "55vh",
                        overflow: "scroll",
                        "& .MuiTableCell-root": {
                            fontSize: 13,
                            whiteSpace: "nowrap",
                            py: 0.1,
                            borderRadius: "0 !important",
                        },
                        "& .MuiTableRow-root": {
                            "&": {
                                borderBottom: "1px solid " + theme.palette.action.hover,
                            },
                            "&:last-of-type": {
                                borderBottom: "none",
                            },
                        },
                    }}
                >
                    <Table size="small" sx={{ position: "relative" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    data-cy={`header-task-${type}`}
                                    sx={{ position: "sticky", left: 0, zIndex: 10 }}
                                >
                                    <SortOn field="title"> Task</SortOn>
                                </TableCell>
                                <TableCell data-cy={`header-schedule-${type}`}>
                                    <SortOn field="scheduleTitle">Schedule</SortOn>
                                </TableCell>
                                <TableCell data-cy={`header-version-${type}`}>Version</TableCell>
                                <TableCell data-cy={`header-where-${type}`}>
                                    <SortOn field="where">Where</SortOn>
                                </TableCell>
                                <TableCell data-cy={`header-asset-${type}`}>Asset</TableCell>
                                <TableCell data-cy={`header-frequency-${type}`}>
                                    <SortOn field="frequency.label">Frequency</SortOn>
                                </TableCell>
                                <TableCell data-cy={`header-duration-${type}`}>
                                    <SortOn field="minutes">Duration</SortOn>
                                </TableCell>
                                <TableCell data-cy={`header-skill-${type}`}>
                                    <SortOn field="skill">Skill</SortOn>
                                </TableCell>
                                <TableCell data-cy={`header-criticality-${type}`}>
                                    <SortOn field="classification">Criticality</SortOn>
                                </TableCell>
                                <TableCell data-cy={`header-annual-duration-${type}`}>
                                    <SortOn field="annualMinutes">Annual Duration</SortOn>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <RequestPageRepeat
                            showTop
                            pageSize={take}
                            showBottom={false}
                            fallback={
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <NoResults />
                                    </TableCell>
                                </TableRow>
                            }
                            setSkip={setSkip}
                            Component={TableBody}
                            list={results.tasks.items}
                            item={<TaskItem />}
                            total={results.tasks.total}
                        />
                    </Table>
                </Box>
            </Box>
        </Bound>
    )
}
